import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../../components/OnboardingComponents/CustomIconButton"
import TruncatedText from "../../../components/Common/TruncatedText"
import { ICONS } from "../../../constants/icons"
import { toastApiError } from "../../../helpers/apiError"
import {
  downloadArtsGenomeCsvTemplate,
  getArtsGenomeInitials,
  parseArtsGenomeCsvNames,
} from "./adminArtsGenomeData"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const AVATAR_TONES = ["coral", "sand", "mint", "peach", "lilac"]

const readFileAsText = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ""))
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"))
    reader.readAsText(file)
  })

/**
 * Upload Bulk CSV modal — Figma (`40005313:45894` upload, listing preview).
 * Step 1: pick CSV → Step 2: review/remove names → Upload posts `{ names }`.
 */
const UploadBulkModal = ({
  isOpen,
  onClose,
  onUpload,
  columnLabel = "Spellbook",
  failMessage = "Failed to upload items",
  templateExamples = [],
  templateFilename = "bulk-template.csv",
}) => {
  const fileInputRef = useRef(null)
  const [step, setStep] = useState("upload")
  const [selectedFile, setSelectedFile] = useState(null)
  const [rows, setRows] = useState([])
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setStep("upload")
      setSelectedFile(null)
      setRows([])
      setProcessing(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }, [isOpen])

  const handleFileChange = event => {
    const file = event.target.files?.[0]
    if (!file) return

    const name = String(file.name || "").toLowerCase()
    const isCsv =
      name.endsWith(".csv") ||
      String(file.type || "").toLowerCase().includes("csv")

    if (!isCsv) {
      toast.error("Only CSV files are allowed.", TOAST_OPTIONS)
      event.target.value = ""
      return
    }

    setSelectedFile(file)
  }

  const handleProceed = async () => {
    if (!selectedFile) {
      toast.error("Select a CSV file to continue.", TOAST_OPTIONS)
      return
    }

    setProcessing(true)
    try {
      const text = await readFileAsText(selectedFile)
      const names = parseArtsGenomeCsvNames(text)

      if (names.length === 0) {
        toast.error(
          "No valid names found in this CSV. Use one name per row.",
          TOAST_OPTIONS,
        )
        return
      }

      setRows(
        names.map((name, index) => ({
          id: `${index}-${name}`,
          name,
          initials: getArtsGenomeInitials(name),
          tone: AVATAR_TONES[index % AVATAR_TONES.length],
        })),
      )
      setStep("preview")
    } catch (err) {
      toastApiError(err, "Failed to read CSV file")
    } finally {
      setProcessing(false)
    }
  }

  const handleRemove = rowId => {
    setRows(prev => prev.filter(row => row.id !== rowId))
  }

  const handleUpload = async () => {
    const names = rows.map(row => row.name).filter(Boolean)
    if (names.length === 0) {
      toast.error("Add at least one item to upload.", TOAST_OPTIONS)
      return
    }

    setProcessing(true)
    try {
      await onUpload?.(names)
      onClose()
    } catch (err) {
      toastApiError(err, failMessage)
    } finally {
      setProcessing(false)
    }
  }

  const handleCancel = () => {
    if (processing) return
    onClose()
  }

  const handleDownloadTemplate = event => {
    event.preventDefault()
    event.stopPropagation()
    downloadArtsGenomeCsvTemplate({
      columnLabel,
      examples: templateExamples,
      filename: templateFilename,
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      centered
      className={`amok-modal amok-modal--password admin-arts-genome-bulk-modal${
        step === "preview" ? " admin-arts-genome-bulk-modal--preview" : ""
      }`}
      contentClassName="amok-modal__content admin-arts-genome-bulk-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body admin-arts-genome-bulk-modal__body">
        <div className="admin-arts-genome-bulk-modal__intro">
          <h3 className="admin-arts-genome-bulk-modal__title">Upload Bulk</h3>
        </div>

        {step === "upload" ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="admin-arts-genome-bulk-modal__file-input"
              onChange={handleFileChange}
            />

            <button
              type="button"
              className="admin-arts-genome-bulk-modal__dropzone"
              onClick={() => fileInputRef.current?.click()}
              disabled={processing}
            >
              <span className="admin-arts-genome-bulk-modal__upload-btn">
                Upload File
              </span>
              <span className="admin-arts-genome-bulk-modal__hint">
                Only CSV files are allowed
              </span>
              {selectedFile ? (
                <span className="admin-arts-genome-bulk-modal__filename">
                  {selectedFile.name}
                </span>
              ) : null}
            </button>

            <div className="admin-arts-genome-bulk-modal__format">
              <p className="admin-arts-genome-bulk-modal__format-text">
                CSV format: one <strong>{columnLabel}</strong> per row. First
                row can be the header{" "}
                <code className="admin-arts-genome-bulk-modal__code">
                  {columnLabel}
                </code>
                .
              </p>
              <button
                type="button"
                className="admin-arts-genome-bulk-modal__template-link"
                onClick={handleDownloadTemplate}
                disabled={processing}
              >
                Download CSV template
              </button>
            </div>

            <div className="admin-arts-genome-bulk-modal__actions">
              <CustomButton
                variant="danger"
                size="md"
                type="button"
                onClick={handleCancel}
                disabled={processing}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="primary"
                size="md"
                type="button"
                onClick={handleProceed}
                loading={processing}
                disabled={processing || !selectedFile}
              >
                Proceed
              </CustomButton>
            </div>
          </>
        ) : (
          <>
            <div className="admin-arts-genome-bulk-modal__table" role="table">
              <div
                className="admin-arts-genome-bulk-modal__table-row admin-arts-genome-bulk-modal__table-row--head"
                role="row"
              >
                <div role="columnheader">{columnLabel}</div>
                <div
                  role="columnheader"
                  className="admin-arts-genome-bulk-modal__action-head"
                >
                  Action
                </div>
              </div>

              <div className="admin-arts-genome-bulk-modal__table-body">
                {rows.map(row => (
                  <div
                    key={row.id}
                    className="admin-arts-genome-bulk-modal__table-row"
                    role="row"
                  >
                    <div
                      className="admin-arts-genome-bulk-modal__name-cell"
                      role="cell"
                    >
                      <span
                        className={`admin-arts-genome-bulk-modal__avatar admin-arts-genome-bulk-modal__avatar--${row.tone}`}
                        aria-hidden="true"
                      >
                        {row.initials}
                      </span>
                      <TruncatedText className="admin-arts-genome-bulk-modal__name">
                        {row.name}
                      </TruncatedText>
                    </div>
                    <div
                      className="admin-arts-genome-bulk-modal__row-action"
                      role="cell"
                    >
                      <CustomIconButton
                        iconSrc={ICONS.trash}
                        label={`Remove ${row.name}`}
                        tone="danger"
                        size="sm"
                        disabled={processing}
                        onClick={() => handleRemove(row.id)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <p className="admin-arts-genome-bulk-modal__total">
              Total Results Found:{" "}
              <span className="admin-arts-genome-bulk-modal__total-count">
                {rows.length}
              </span>
            </p>

            <div className="admin-arts-genome-bulk-modal__actions">
              <CustomButton
                variant="danger"
                size="md"
                type="button"
                onClick={handleCancel}
                disabled={processing}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="primary"
                size="md"
                type="button"
                onClick={handleUpload}
                loading={processing}
                disabled={processing || rows.length === 0}
              >
                Upload
              </CustomButton>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  )
}

UploadBulkModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
  columnLabel: PropTypes.string,
  failMessage: PropTypes.string,
  templateExamples: PropTypes.arrayOf(PropTypes.string),
  templateFilename: PropTypes.string,
}

UploadBulkModal.defaultProps = {
  onUpload: undefined,
  columnLabel: "Spellbook",
  failMessage: "Failed to upload items",
  templateExamples: [],
  templateFilename: "bulk-template.csv",
}

export default UploadBulkModal
